/**
 * Chairman Reports Page
 * Generic reports page with navigation to specific report types
 */
import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/Layout/MainLayout';
import { 
  MdPeople, 
  MdBarChart, 
  MdMic, 
  MdSchool, 
  MdFlightTakeoff, 
  MdAttachMoney 
} from 'react-icons/md';

const ChairmanReportsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const reportTypes = [
    {
      id: 'hr-performance',
      title: 'HR Performance Report',
      description: 'View HR productivity and performance metrics',
      icon: MdPeople,
      path: '/chairman/reports/hr-performance',
      color: 'blue'
    },
    {
      id: 'summary',
      title: 'Summary Report',
      description: 'Comprehensive overview of all business metrics',
      icon: MdBarChart,
      path: '/chairman/reports/summary',
      color: 'green'
    },
    {
      id: 'interview',
      title: 'Interview Report',
      description: 'Interview statistics and outcomes',
      icon: MdMic,
      path: '/chairman/reports/interview',
      color: 'purple'
    },
    {
      id: 'exam',
      title: 'Exam Report',
      description: 'Exam results and performance data',
      icon: MdSchool,
      path: '/chairman/reports/exam',
      color: 'orange'
    },
    {
      id: 'departure',
      title: 'Departure Report',
      description: 'Departed candidates, country-wise counts, and full details',
      icon: MdFlightTakeoff,
      path: '/chairman/reports/departure',
      color: 'indigo'
    },
    {
      id: 'payment',
      title: 'Payment Report',
      description: 'Financial statistics and revenue data',
      icon: MdAttachMoney,
      path: '/chairman/reports/payment',
      color: 'emerald'
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700',
      green: 'bg-green-50 hover:bg-green-100 border-green-200 text-green-700',
      purple: 'bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700',
      orange: 'bg-orange-50 hover:bg-orange-100 border-orange-200 text-orange-700',
      indigo: 'bg-indigo-50 hover:bg-indigo-100 border-indigo-200 text-indigo-700',
      emerald: 'bg-emerald-50 hover:bg-emerald-100 border-emerald-200 text-emerald-700'
    };
    return colorMap[color] || colorMap.blue;
  };

  if (user?.role !== 'CHAIRMAN') {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-gray-600">Access Denied</h2>
            <p className="text-gray-500 mt-2">This page is only accessible to Chairman users.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Chairman Reports</h1>
          <p className="text-gray-600">Access comprehensive business reports and analytics</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reportTypes.map((report) => (
            <div
              key={report.id}
              onClick={() => navigate(report.path)}
              className={`p-6 rounded-lg border-2 cursor-pointer transition-all duration-200 ${getColorClasses(report.color)}`}
            >
              <div className="flex items-center mb-4">
                <report.icon className="text-2xl mr-3" />
                <h3 className="text-lg font-semibold">{report.title}</h3>
              </div>
              <p className="text-sm opacity-80">{report.description}</p>
              <div className="mt-4 flex items-center text-sm font-medium">
                View Report
                <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Stats */}
        <div className="mt-12 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-700">6</div>
              <div className="text-sm text-gray-600">Available Reports</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-700">Real-time</div>
              <div className="text-sm text-gray-600">Data Updates</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-700">CSV</div>
              <div className="text-sm text-gray-600">Export Format</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-700">Filtered</div>
              <div className="text-sm text-gray-600">Data Views</div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ChairmanReportsPage;